import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

type Tone = "primary" | "emerald" | "amber" | "rose" | "slate";

const toneClasses: Record<Tone, string> = {
  primary: "bg-primary/12 text-primary",
  emerald: "bg-emerald-500/15 text-emerald-300",
  amber: "bg-amber-500/15 text-amber-300",
  rose: "bg-primary/12 text-primary",
  slate: "bg-muted text-muted-foreground",
};

/**
 * Layout wrapper for every interior page.
 *
 * Full width by default: these are working surfaces (tables, listing grids,
 * moderation queues) that were being squeezed into a centred ~1150px column
 * with dead space either side on any normal monitor. Pass `centered` for the
 * rare narrow-measure page (long-form reading) that actually wants the cap.
 */
export function PageShell({ children, className, centered }: { children: ReactNode; className?: string; centered?: boolean }) {
  return (
    <main className={cn(
      "space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8",
      centered ? "mx-auto max-w-6xl" : "w-full",
      className,
    )}>
      {children}
    </main>
  );
}

/**
 * The Dark · Gold interior hero. One implementation for every interior page —
 * AI Deal Matching, Deal CRM, Resources, Favorites, Interests — so they cannot
 * drift into slightly different headers again.
 *
 * `icon` and `titleAccent` are optional so pages that predate them keep their
 * exact current look: no icon renders the small dot, no accent renders a plain
 * title.
 */
export function PageHero({
  eyebrow,
  title,
  titleAccent,
  description,
  icon: Icon,
  actions,
  aside,
}: {
  eyebrow: string;
  title: string;
  /** Rendered after `title` in the gold gradient, e.g. "AI |Deal Matching|". */
  titleAccent?: string;
  description: string;
  icon?: LucideIcon;
  actions?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="public-hero-shell rounded-[1.85rem] px-6 py-8 text-foreground shadow-glow sm:px-8 sm:py-9">
      <div className="public-grid-lattice absolute inset-0 opacity-45" />
      <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
      <div className="relative z-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
        <div>
          <div className="public-pill">
            {Icon ? <Icon className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
            {eyebrow}
          </div>
          <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl">
            {title}
            {titleAccent && <> <span className="text-gradient">{titleAccent}</span></>}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">{description}</p>
          {actions && <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">{actions}</div>}
        </div>

        {aside && (
          <div className="public-glass-card rounded-[1.35rem] p-5">
            {aside}
          </div>
        )}
      </div>
    </section>
  );
}

export function PagePanel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-2xl border border-border/80 bg-card p-5 shadow-card sm:p-6", className)}>
      {children}
    </section>
  );
}

export function PanelHeading({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">{title}</h2>
        {description && <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>}
      </div>
      {actions}
    </div>
  );
}

export function MetricTile({
  label,
  value,
  icon: Icon,
  tone = "primary",
  href,
}: {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  tone?: Tone;
  href?: string;
}) {
  const content = (
    <div className="h-full rounded-xl border border-border/75 bg-card p-4 shadow-sm transition-shadow duration-200 hover:shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
          <div className="mt-2 text-2xl font-extrabold tracking-tight text-foreground">{value}</div>
        </div>
        <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg", toneClasses[tone])}>
          <Icon className="h-4.5 w-4.5" />
        </div>
      </div>
    </div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }

  return content;
}

export function EmptyPanel({
  title,
  description,
  icon: Icon,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-border/80 bg-muted/20 px-6 py-12 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-xl bg-muted/70 text-muted-foreground/55">
        <Icon className="h-8 w-8" />
      </div>
      <h3 className="mt-4 text-xl font-bold tracking-tight text-foreground">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}